import { useState, useCallback, useEffect, useRef } from 'react';
import type { MoodContext, MoodConfig } from '@/types';

// Mood configurations - matches backend MOOD_PROFILES
export const MOOD_CONFIGS: MoodConfig[] = [
  { mood: 'energetic', label: 'Energetic', emoji: '⚡', description: 'High energy upbeat music' },
  { mood: 'chill', label: 'Chill', emoji: '☕', description: 'Relaxed and mellow vibes' },
  { mood: 'happy', label: 'Happy', emoji: '😊', description: 'Uplifting positive music' },
  { mood: 'sad', label: 'Sad', emoji: '😢', description: 'Melancholic emotional music' },
  { mood: 'focus', label: 'Focus', emoji: '🎯', description: 'Instrumental music for concentration' },
  { mood: 'party', label: 'Party', emoji: '🎉', description: 'Dance and party music' },
  { mood: 'workout', label: 'Workout', emoji: '💪', description: 'High tempo workout music' },
  { mood: 'angry', label: 'Angry', emoji: '😡', description: 'High energy aggressive music' },
  { mood: 'relax', label: 'Relax', emoji: '🧘', description: 'Calm and peaceful music' },
  { mood: 'melancholic', label: 'Melancholic', emoji: '🌧️', description: 'Introspective contemplative music' },
];

// Global mood state (simple module-level state for sharing between components)
let globalMood: MoodContext | null = null;
const listeners = new Set<(mood: MoodContext | null) => void>();

const notifyListeners = () => {
  listeners.forEach(listener => listener(globalMood));
};

export const getSelectedMood = () => globalMood;

interface UseMoodEngineReturn {
  selectedMood: MoodContext | null;
  setMood: (mood: MoodContext) => void;
  clearMood: () => void;
  isSettingMood: boolean;
  error: Error | null;
  getMoodConfig: (mood: MoodContext) => MoodConfig | undefined;
  allMoods: MoodConfig[];
}

/**
 * Hook to manage mood selection and activation
 */
export const useMoodEngine = (): UseMoodEngineReturn => {
  const [selectedMood, setSelectedMood] = useState<MoodContext | null>(globalMood);
  const [isSettingMood] = useState(false);
  const [error] = useState<Error | null>(null);

  // Use ref to store the listener so we can properly clean it up
  const listenerRef = useRef<((mood: MoodContext | null) => void) | null>(null);

  // Subscribe to global mood changes
  useEffect(() => {
    // Create listener function
    const listener = (mood: MoodContext | null) => {
      setSelectedMood(mood);
    };

    // Store in ref for cleanup
    listenerRef.current = listener;

    // Add to listeners set (Set automatically handles duplicates)
    listeners.add(listener);

    // Sync with current global mood on mount
    if (globalMood !== null) {
      setSelectedMood(globalMood);
    }

    return () => {
      // Remove from listeners on unmount
      if (listenerRef.current) {
        listeners.delete(listenerRef.current);
      }
    };
  }, []);

  const setMood = useCallback((mood: MoodContext) => {
    // Set global mood first
    globalMood = mood;
    // Update local state immediately
    setSelectedMood(mood);
    // Notify all other listeners
    notifyListeners();
  }, []);

  const clearMood = useCallback(() => {
    globalMood = null;
    setSelectedMood(null);
    notifyListeners();
  }, []);

  const getMoodConfig = useCallback((mood: MoodContext): MoodConfig | undefined => {
    return MOOD_CONFIGS.find(config => config.mood === mood);
  }, []);

  return {
    selectedMood,
    setMood,
    clearMood,
    isSettingMood,
    error,
    getMoodConfig,
    allMoods: MOOD_CONFIGS,
  };
};
